import { Close } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import {
  TRACKING_EVENT_TYPE_EMAIL_REPLY,
  TRACKING_EVENT_TYPE_SCHEDULE_BOOKING,
} from "../../../constants";
import { hostname, parseError } from "../../../utils";

interface MemberOption {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

interface StepOption {
  stepId?: string;
  step_id?: string;
  stepOrder?: number;
  step_order?: number;
  stepName?: string;
  step_name?: string;
  step_subject?: string;
  stepSubject?: string;
  actionType?: string;
  step_action_type?: string;
}

interface AddManualMetricDialogProps {
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
  sequenceId: string;
  companyId: string;
  steps: StepOption[];
  getToken: () => Promise<string>;
}

const memberLabel = (m: MemberOption): string => {
  const name = [m.firstName, m.lastName].filter(Boolean).join(" ").trim();
  if (name && m.email) return `${name} (${m.email})`;
  return name || m.email || m.id;
};

const stepKey = (s: StepOption) => s.stepId || s.step_id || "";

const stepLabel = (s: StepOption) => {
  const order = s.stepOrder || s.step_order;
  const name =
    s.stepName || s.step_name || s.stepSubject || s.step_subject || "Step";
  return order ? `Step ${order}: ${name}` : name;
};

const AddManualMetricDialog: React.FC<AddManualMetricDialogProps> = ({
  open,
  onClose,
  onAdded,
  sequenceId,
  companyId,
  steps,
  getToken,
}) => {
  const [eventType, setEventType] = useState<string>(
    TRACKING_EVENT_TYPE_SCHEDULE_BOOKING
  );
  const [stepId, setStepId] = useState<string>("");
  const [memberSearch, setMemberSearch] = useState("");
  const [memberOptions, setMemberOptions] = useState<MemberOption[]>([]);
  const [memberLoading, setMemberLoading] = useState(false);
  const [selectedMember, setSelectedMember] = useState<MemberOption | null>(
    null
  );
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setEventType(TRACKING_EVENT_TYPE_SCHEDULE_BOOKING);
      setStepId(steps.length > 0 ? stepKey(steps[0]) : "");
      setMemberSearch("");
      setMemberOptions([]);
      setSelectedMember(null);
      setNote("");
      setError(null);
    }
  }, [open, steps]);

  // Debounced member search
  useEffect(() => {
    if (!open) return;
    const handle = setTimeout(async () => {
      setMemberLoading(true);
      try {
        const token = await getToken();
        const res = await axios.get(
          `${hostname}/sequences/${sequenceId}/members`,
          {
            params: {
              companyId,
              page: 1,
              pageSize: 25,
              search: memberSearch || undefined,
            },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setMemberOptions(res.data?.items || []);
      } catch (err) {
        // Silent — search-as-you-type
      } finally {
        setMemberLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [open, memberSearch, sequenceId, companyId, getToken]);

  const stepOptions = useMemo(
    () => steps.filter((s) => stepKey(s)),
    [steps]
  );

  const canSubmit =
    !!selectedMember && !!stepId && !!eventType && !submitting;

  const handleSubmit = async () => {
    if (!selectedMember) return;
    setSubmitting(true);
    setError(null);
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/sequences/${sequenceId}/manual-metric`,
        {
          memberId: selectedMember.id,
          stepId,
          eventType,
          note: note.trim() || undefined,
        },
        {
          params: { companyId },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      onAdded();
      onClose();
    } catch (err) {
      setError(parseError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Add Metric</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Manually attribute a booking or reply to a sequence step. Useful when
          a recipient takes action off-channel — for example, booking a meeting
          on your website after seeing a sequence email.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="manual-metric-type-label">Metric type</InputLabel>
          <Select
            labelId="manual-metric-type-label"
            label="Metric type"
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
          >
            <MenuItem value={TRACKING_EVENT_TYPE_SCHEDULE_BOOKING}>
              Booking (meeting booked)
            </MenuItem>
            <MenuItem value={TRACKING_EVENT_TYPE_EMAIL_REPLY}>
              Reply (positive reply)
            </MenuItem>
          </Select>
        </FormControl>

        <Autocomplete
          options={memberOptions}
          loading={memberLoading}
          value={selectedMember}
          onChange={(_, value) => setSelectedMember(value)}
          getOptionLabel={(option) => memberLabel(option)}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          filterOptions={(x) => x}
          onInputChange={(_, value, reason) => {
            if (reason === "input") setMemberSearch(value);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Member"
              placeholder="Search by name or email"
              sx={{ mb: 2 }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {memberLoading ? (
                      <CircularProgress color="inherit" size={18} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="manual-metric-step-label">Step</InputLabel>
          <Select
            labelId="manual-metric-step-label"
            label="Step"
            value={stepId}
            onChange={(e) => setStepId(e.target.value)}
          >
            {stepOptions.map((s) => (
              <MenuItem key={stepKey(s)} value={stepKey(s)}>
                {stepLabel(s)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Note (optional)"
          placeholder="e.g. booked via website after step 2 email"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          multiline
          minRows={2}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          loading={submitting}
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          Add metric
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddManualMetricDialog;
